import { decamelizeKeys, nullifyValues } from './helpers';
import { IntegrationState } from '@callrail/inti/src/inti/proto/integration';
import { Vendor } from '@callrail/inti/src/inti/proto/vendor';
import {
  VendorConfigType,
  VendorScope,
} from '@callrail/inti/src/inti/proto/vendor';

/**
 * By default our "fetch vendor" endpoint returns a standardized Protobuf
 * response. However until the frontend can be updated to parse this response,
 * we need to provide it with the legacy response it is has historically
 * expected from the CallRail app.
 *
 * The entire purpose of this class is to transform a `Vendor` protobuf object
 * into the same format generated by the CallRail application's
 * `integrations#type` endpoint
 *
 * See: https://github.com/callrail/callrail/blob/master/app/controllers/integrations_controller.rb#L54
 *
 * This same format is used to initialize the `Integration` data model on
 * the looky frontend
 *
 * See: https://github.com/callrail/looky/blob/main/libs/looky/core/src/models/integration.model.ts
 */

/**
 * Transforms an array of `inti.proto.VendorConfig` values into a custom
 * array format that matches the legacy representation the frontend UI expects.
 *
 * Input:
 *
 *    [
 *      {
 *        "key": "account_id",
 *        "required": false,
 *        "title": "",
 *        ....
 *      },
 *      ....
 *    ]
 *
 * Output:
 *
 *    [
 *      {
 *        "key": "account_id",
 *        "value": {
 *          "required": false,
 *          "title": "",
 *          ....
 *      },
 *      ....
 *    ]
 *
 * @see https://github.com/callrail/inti/blob/main/gems/inti-client/lib/inti/proto/vendor.proto
 * @return [Array<VendorConfig>] Custom formatted array of objects
 * @param configs
 */
function toConfigArray(configs: any[] | null | undefined) {
  const result = [];

  if (configs == null) {
    return result;
  }

  configs.forEach((config) => {
    // Shallow clone!
    const cfg = { ...config };

    // Remove the `key` from the config
    const key = cfg.key;
    delete cfg.key;

    /**
     * Translate `type` into it's Protobuf value
     * e.g. "string", "datetime", etc..
     */
    // eslint-disable-next-line @typescript-eslint/dot-notation
    cfg['type'] = VendorConfigType[cfg.type]?.toLowerCase();
    result.push({ key, value: decamelizeKeys(cfg) });
  });

  return result;
}

/**
 * Transforms an array of `inti.proto.VendorConfig` values into a custom
 * object format that matches the legacy representation the frontend UI expects.
 *
 * Input:
 *
 *    [
 *      {
 *        "key": "account_id",
 *        "required": false,
 *        "title": "",
 *        ....
 *      },
 *      ....
 *    ]
 *
 * Output:
 *
 *    {
 *      "account_id": {
 *        "required": false,
 *        "title": "",
 *        ....
 *      },
 *      ....
 *    }
 *
 * @see https://github.com/callrail/inti/blob/main/gems/inti-client/lib/inti/proto/vendor.proto
 * @param configs [Array<VendorConfig>] list of vendor configs, as defined in
 *   Protobuf (inti.proto.VendorConfig)
 * @return Custom formatted object
 */
function toConfigMap(configs: any[] | null | undefined) {
  const result = {};

  if (configs == null) {
    return result;
  }

  configs.forEach((config) => {
    // Shallow clone!
    const cfg = { ...config };

    // Remove the `key` from the config
    const key = cfg.key;
    delete cfg.key;

    /**
     * Translate `type` into its Protobuf value
     * e.g. "string", "datetime", etc..
     */
    // eslint-disable-next-line @typescript-eslint/dot-notation
    cfg['type'] = VendorConfigType[cfg.type]?.toLowerCase();

    // Add entry to the result set
    result[key] = decamelizeKeys(cfg);
  });

  return result;
}

/**
 * Transforms an `inti.proto.Vendor` protobuf object into a custom format
 * to match the legacy representation the frontend UI expects
 *
 * This data structure tries to match the response generated by the
 * `integrations#type` endpoint inside the CallRail monolith. Once all
 * integrations are migrated to `Inti`, that endpoint and this legacy functionality
 * can be deprecated.
 *
 * @see https://github.com/callrail/callrail/blob/master/app/controllers/integrations_controller.rb#L54
 * @param vendor [Vendor] a vendor object, as defined in Protobuf (inti.proto.Vendor)
 * @return Custom formatted object
 */
export default function legacyTransformFetchVendor(vendor: Vendor): any {
  const instance = vendor?.instance;

  return nullifyValues({
    about: vendor.aboutTemplate,
    advanced_config: toConfigArray(vendor.advancedConfig),
    advanced_form: null,
    config: toConfigMap(vendor.basicConfig),
    config_array: toConfigArray(vendor.basicConfig),
    description: vendor.description,
    enabled_id: instance?.integrationId,
    failed_message: vendor.failedMessage,
    flipper_flag: vendor.flipperFlag,
    form_path: vendor.formTemplate,
    header_template: vendor.headerTemplate,
    help: vendor.helpTemplate,
    id: vendor.id,
    image: vendor.image,
    name: vendor.name,
    ng_route: vendor.ngRoute,
    popular: vendor.popular,
    premium: vendor.premium,
    requires_auth: vendor.requiresAuth,
    safe_name: vendor.safeName,
    site_name: instance?.whiteLabel?.siteName,
    state: IntegrationState[instance?.integrationState],
    support_link: vendor.supportLink,
    type: VendorScope[vendor.scope]?.toLowerCase(),
    upsell_template: vendor.upsellTemplate,
    white_label: (instance?.whiteLabel || {}).isEnabled,
  });
}
